import { ReactNode, useEffect, useState, createContext, useContext } from 'react';
import { Settings, loadSettings } from '../utils/settings';
import Loading from '../components/Loading';

interface SettingsContextType {
  settings: Settings | null;
  isLoading: boolean;
  error: string | null;
}

const SettingsContext = createContext<SettingsContextType>({
  settings: null,
  isLoading: true,
  error: null,
});

export function useSettings(): SettingsContextType {
  return useContext(SettingsContext);
}

interface SettingsProviderProps {
  children: ReactNode;
}

export function SettingsProvider({ children }: SettingsProviderProps) {
  const [state, setState] = useState<SettingsContextType>({
    settings: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    loadSettings()
      .then((settings) => {
        setState({ settings, isLoading: false, error: null });
      })
      .catch((error) => {
        setState({
          settings: null,
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to load settings',
        });
      });
  }, []);

  if (state.isLoading) {
    return <Loading message="Loading settings..." />;
  }

  if (state.error) {
    return <div>Error loading settings: {state.error}</div>;
  }

  return <SettingsContext.Provider value={state}>{children}</SettingsContext.Provider>;
}
